import type {
  RefundCreateRequest,
  RefundResponse,
  RefundUpdateRequest,
  RefundListParams,
  RefundListResponse,
  RefundAggregateResponse,
} from '../types';
import type { RequestFn } from '../client';

/** Create and manage refunds for completed payments. */
export class Refunds {
  constructor(private readonly request: RequestFn) {}

  /**
   * Create a refund for a payment.
   *
   * Dashboard-initiated refunds are subject to the caller's operation-limit
   * rule, resolved against the role the request authenticated with. An
   * over-limit refund either fails with `DE_01` (the rule blocks) or with
   * HTTP 409 `DE_06` — the rule requires approval, and `DelopayError.data`
   * carries `PendingApprovalErrorDetails`. No refund exists in either case;
   * `DE_06` names one that a second approver can still let through, via
   * `operationLimits.approve()`.
   *
   * @param params - Refund parameters, including the required `payment_id` and optional amount.
   * @returns The created refund.
   *
   * @example
   * ```typescript
   * const refund = await delopay.refunds.create({
   *   payment_id: 'pay_abc123',
   *   amount: 2500, // partial refund of 25.00 EUR
   * });
   * ```
   */
  async create(params: RefundCreateRequest): Promise<RefundResponse> {
    return this.request('POST', '/refunds', { body: params });
  }

  /**
   * Retrieve a refund by its ID.
   *
   * @param refundId - The unique refund ID.
   * @returns The refund.
   *
   * @example
   * ```typescript
   * const refund = await delopay.refunds.retrieve('ref_abc123');
   * ```
   */
  async retrieve(refundId: string): Promise<RefundResponse> {
    return this.request('GET', `/refunds/${encodeURIComponent(refundId)}`);
  }

  /**
   * Update the reason or metadata on an existing refund.
   *
   * @param refundId - The refund ID to update.
   * @param params - Fields to update (reason, metadata).
   * @returns The updated refund.
   */
  async update(refundId: string, params: RefundUpdateRequest): Promise<RefundResponse> {
    return this.request('POST', `/refunds/${encodeURIComponent(refundId)}`, { body: params });
  }

  /**
   * List refunds, optionally filtered by payment, status, or date range.
   *
   * @param params - Optional filter and pagination parameters.
   * @returns Paginated list of refunds.
   */
  async list(params?: RefundListParams): Promise<RefundListResponse> {
    return this.request('POST', '/refunds/list', { body: params });
  }

  // --- OLAP extensions (Task 4.3) ---

  /** List refunds (profile-scoped). `POST /refunds/profile/list` */
  async listByProfile(params?: RefundListParams): Promise<RefundListResponse> {
    return this.request('POST', '/refunds/profile/list', { body: params });
  }

  /** Get refund filter options. `GET /refunds/filter` */
  async getFilters(
    params?: Record<string, string | number | undefined>,
  ): Promise<Record<string, unknown>> {
    return this.request('GET', '/refunds/filter', { query: params });
  }

  /** Get refund aggregates. `GET /refunds/aggregate` */
  async aggregate(
    params?: Record<string, string | number | undefined>,
  ): Promise<RefundAggregateResponse> {
    return this.request('GET', '/refunds/aggregate', { query: params });
  }

  /** Get refund aggregates (profile-scoped). `GET /refunds/profile/aggregate` */
  async aggregateByProfile(
    params?: Record<string, string | number | undefined>,
  ): Promise<RefundAggregateResponse> {
    return this.request('GET', '/refunds/profile/aggregate', { query: params });
  }

  /** Manually update refund status. `PUT /refunds/{refundId}/manual-update` */
  async manualUpdate(refundId: string, params: Record<string, unknown>): Promise<RefundResponse> {
    return this.request('PUT', `/refunds/${encodeURIComponent(refundId)}/manual-update`, {
      body: params,
    });
  }
}
